/**
 * User Create/Edit Modal
 * 
 * Comprehensive modal for creating and editing users with role and clinic assignment
 */

import { useState, useEffect } from 'react'
import { userAPI, clinicAPI, apiHelpers } from '../services/api'
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from './ui/dialog'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { Checkbox } from './ui/checkbox'
import { useAuthStore } from '../stores/authStore'
import { generatePassword } from '../utils/passwordGenerator'

const ROLE_OPTIONS = [
  { value: 'clinic_admin', label: 'Clinic Admin', description: 'Full control of assigned clinics' },
  { value: 'physician_manager', label: 'Physician Manager', description: 'Manage physicians and view analytics' },
  { value: 'pharmacy_manager', label: 'Pharmacy Manager', description: 'Manage pharmacies and programs' },
  { value: 'program_manager', label: 'Program Manager', description: 'Manage treatment programs' },
  { value: 'data_entry', label: 'Data Entry', description: 'Create and update records only' },
  { value: 'viewer', label: 'View All', description: 'Read-only access to all modules' },
  { value: 'report_viewer', label: 'Report Viewer', description: 'Reports and analytics only (no config access)' },
  { value: 'tv_display', label: 'TV Display', description: 'Full-screen dashboard for office TVs' }
]

const DATE_RANGE_OPTIONS = [
  { value: 'last7', label: 'Last 7 Days' },
  { value: 'last14', label: 'Last 14 Days' },
  { value: 'last30', label: 'Last 30 Days' },
  { value: 'last90', label: 'Last 90 Days' },
  { value: 'mtd', label: 'Month to Date' },
  { value: 'ytd', label: 'Year to Date' }
]

import ReportPagePermissions from './ReportPagePermissions'

export default function UserModal({ isOpen, onClose, onSave, user = null }) {
  const { user: currentUser } = useAuthStore()
  const isEdit = !!user
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    role: '',
    clinicIds: [],
    isActive: true,
    metadata: {
      notes: ''
    },
    tvDisplaySettings: {
      displayClinicIds: [],
      dateRange: 'last30',
      refreshInterval: 15
    }
  })
  
  const [availableClinics, setAvailableClinics] = useState([])
  const [loadingClinics, setLoadingClinics] = useState(false)
  const [validationErrors, setValidationErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [passwordCopied, setPasswordCopied] = useState(false)

  const handleGeneratePassword = async () => {
    const newPassword = generatePassword()
    setFormData(prev => ({
      ...prev,
      password: newPassword,
      confirmPassword: newPassword
    }))
    setShowPassword(true)
    setValidationErrors(prev => {
      const { password, confirmPassword, ...rest } = prev
      return rest
    })

    // Copy to clipboard so it can be shared with the new user
    try {
      await navigator.clipboard.writeText(newPassword)
      setPasswordCopied(true)
      setTimeout(() => setPasswordCopied(false), 2000)
    } catch {
      // Clipboard unavailable (e.g. non-HTTPS) — password is still visible in the field
    }
  }

  // Load form data when editing
  useEffect(() => {
    if (user && isOpen) {
      setFormData({
        email: user.email || '',
        password: '', // Don't populate password for security
        confirmPassword: '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        role: user.role || '',
        clinicIds: user.clinicIds || [],
        isActive: user.isActive !== undefined ? user.isActive : true,
        permissions: user.permissions || {},
        metadata: {
          notes: user.metadata?.notes || ''
        },
        tvDisplaySettings: user.tvDisplaySettings || {
          displayClinicIds: [],
          dateRange: 'last30',
          refreshInterval: 15
        }
      })
    } else if (!user && isOpen) {
      // Reset form for new user
      setFormData({
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        role: '',
        clinicIds: [],
        isActive: true,
        permissions: {},
        metadata: {
          notes: ''
        },
        tvDisplaySettings: {
          displayClinicIds: [],
          dateRange: 'last30',
          refreshInterval: 15
        }
      })
    }
    setValidationErrors({})
  }, [user, isOpen])

  // Auto-select all clinics for super_admin
  useEffect(() => {
    if (formData.role === 'super_admin' && availableClinics.length > 0) {
      const allClinicIds = availableClinics.map(c => c.clinicId)
      setFormData(prev => ({ ...prev, clinicIds: allClinicIds }))
    }
  }, [formData.role, availableClinics])

  // Fetch available clinics
  useEffect(() => {
    if (isOpen) {
      fetchClinics()
    }
  }, [isOpen])

  const fetchClinics = async () => {
    setLoadingClinics(true)
    try {
      const response = await clinicAPI.list()
      if (response.data.success) {
        let clinics = response.data.data || []
        // Clinic admins can only assign users to their own clinics
        if (currentUser?.role === 'clinic_admin' && currentUser?.clinicIds?.length > 0) {
          clinics = clinics.filter(c => currentUser.clinicIds.includes(c.clinicId))
        }
        setAvailableClinics(clinics)
      }
    } catch (error) {
      console.error('Failed to fetch clinics:', error)
      setAvailableClinics([])
    } finally {
      setLoadingClinics(false)
    }
  }

  const updateFormData = (field, value) => {
    setFormData(prev => {
      // Handle nested fields
      if (field.includes('.')) {
        const keys = field.split('.')
        const newData = { ...prev }
        let current = newData
        for (let i = 0; i < keys.length - 1; i++) {
          current[keys[i]] = { ...current[keys[i]] }
          current = current[keys[i]]
        }
        current[keys[keys.length - 1]] = value
        return newData
      }
      return { ...prev, [field]: value }
    })
    
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  const toggleClinic = (clinicId) => {
    setFormData(prev => {
      const clinicIds = prev.clinicIds || []
      if (clinicIds.includes(clinicId)) {
        return { ...prev, clinicIds: clinicIds.filter(id => id !== clinicId) }
      } else {
        return { ...prev, clinicIds: [...clinicIds, clinicId] }
      }
    })
  }

  // A clinic admin can only grant management flags they hold themselves; one
  // with no flags at all is a legacy full-access account. Mirrors
  // denyPermissionGrant in backend/src/utils/userAccess.js. A flag the user
  // already has stays editable so it can be removed.
  const MANAGEMENT_FLAGS = ['canManagePharmacy', 'canManagePrograms', 'canManagePhysicians', 'canManageReleases']
  const canGrant = (permission) => {
    if (currentUser?.role !== 'clinic_admin') return true
    const mine = currentUser.permissions || {}
    if (MANAGEMENT_FLAGS.every(key => mine[key] === undefined)) return true
    return mine[permission] === true || (isEdit && user?.permissions?.[permission] === true)
  }

  const togglePermission = (permission) => {
    setFormData(prev => {
      const permissions = prev.permissions || {}
      return {
        ...prev,
        permissions: {
          ...permissions,
          [permission]: !permissions[permission]
        }
      }
    })
  }

  const toggleTvDisplayClinic = (clinicId) => {
    setFormData(prev => {
      const displayClinicIds = prev.tvDisplaySettings?.displayClinicIds || []
      const newDisplayClinicIds = displayClinicIds.includes(clinicId)
        ? displayClinicIds.filter(id => id !== clinicId)
        : [...displayClinicIds, clinicId]
      return {
        ...prev,
        tvDisplaySettings: {
          ...prev.tvDisplaySettings,
          displayClinicIds: newDisplayClinicIds
        }
      }
    })
  }

  const updateTvDisplaySetting = (field, value) => {
    setFormData(prev => ({
      ...prev,
      tvDisplaySettings: {
        ...prev.tvDisplaySettings,
        [field]: value
      }
    }))
  }

  const validateForm = () => {
    const errors = {}

    // Email validation
    if (!formData.email?.trim()) {
      errors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format'
    }

    // Password validation (only for new users or if changing password)
    if (!isEdit && !formData.password) {
      errors.password = 'Password is required'
    }
    
    if (formData.password) {
      if (formData.password.length < 8) {
        errors.password = 'Password must be at least 8 characters'
      } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
        errors.password = 'Password must contain uppercase, lowercase, and number'
      }
      
      if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match'
      }
    }

    // Name validation
    if (!formData.firstName?.trim()) {
      errors.firstName = 'First name is required'
    }
    
    if (!formData.lastName?.trim()) {
      errors.lastName = 'Last name is required'
    }

    // Role validation
    if (!formData.role) {
      errors.role = 'Role is required'
    }

    // Clinic validation (skip for super_admin as they auto-get all clinics)
    if (formData.role !== 'super_admin' && (!formData.clinicIds || formData.clinicIds.length === 0)) {
      errors.clinicIds = 'At least one clinic must be selected'
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSave = async () => {
    if (!validateForm()) {
      return
    }

    setIsLoading(true)

    try {
      // Prepare data for API
      const userData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        role: formData.role,
        clinicIds: formData.clinicIds,
        isActive: formData.isActive,
        permissions: formData.permissions || {},
        metadata: {
          notes: formData.metadata.notes?.trim() || ''
        }
      }

      // Include TV display settings for tv_display role
      if (formData.role === 'tv_display') {
        userData.tvDisplaySettings = formData.tvDisplaySettings
      }

      // Only include email for new users (backend doesn't allow email changes)
      if (!isEdit) {
        userData.email = formData.email.toLowerCase().trim()
      }

      // Only include password for new users (password changes use separate endpoint)
      if (!isEdit && formData.password) {
        userData.password = formData.password
      }

      console.log('[USER MODAL] Saving user:', userData)

      let response
      if (isEdit) {
        // Use user.id or user._id (API returns id, MongoDB uses _id)
        const userId = user.id || user._id
        response = await userAPI.update(userId, userData)

        // If password was provided, call separate change-password endpoint
        if (formData.password) {
          await userAPI.changePassword(userId, { newPassword: formData.password })
        }
      } else {
        response = await userAPI.create(userData)
      }

      console.log('[USER MODAL] Save response:', response.data)

      if (response.data.success || response.data.data) {
        onSave(response.data.data)
        onClose()
      }
    } catch (error) {
      console.error('Save user error:', error)
      
      // Handle specific errors
      if (error.response?.data?.error) {
        const errorCode = error.response.data.error.code
        const errorMessage = error.response.data.error.message
        
        if (errorCode === 'DUPLICATE_EMAIL') {
          setValidationErrors({ email: 'A user with this email already exists' })
        } else if (errorCode === 'VALIDATION_ERROR') {
          // Check if it's an email-related validation error
          const details = error.response.data.error.details
          const emailError = details?.find(d => d.field === 'email')
          if (emailError) {
            setValidationErrors({ email: emailError.message })
          } else {
            setValidationErrors({ general: errorMessage })
          }
        } else {
          setValidationErrors({ general: errorMessage || 'Failed to save user' })
        }
      } else {
        setValidationErrors({ general: 'Failed to save user. Please try again.' })
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      setValidationErrors({})
      onClose()
    }
  }

  const isSelf = isEdit && user?.id === currentUser?.id

  // Super admin can assign super_admin role, others cannot.
  //
  // A clinic admin can invite a *new* peer admin but cannot promote an existing
  // user into that role — the target may already hold clinics the inviter does
  // not administer, and promoting them would grant admin rights over those.
  // Mirrors ROLE_PROMOTION_DENIED in backend/src/routes/users.js. The user's own
  // current role stays listed so editing an admin never blanks the select.
  const baseRoleOptions = currentUser?.role === 'super_admin'
    ? [{ value: 'super_admin', label: 'Super Admin', description: 'System-wide access' }, ...ROLE_OPTIONS]
    : ROLE_OPTIONS

  const roleOptions = (currentUser?.role !== 'super_admin' && isEdit)
    ? baseRoleOptions.filter(r => r.value !== 'clinic_admin' || user?.role === 'clinic_admin')
    : baseRoleOptions

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit User' : 'Create New User'}</DialogTitle>
          <DialogDescription>
            {isEdit ? 'Update user information and permissions' : 'Add a new user to the system'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* General Error */}
          {validationErrors.general && (
            <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">
              {validationErrors.general}
            </div>
          )}

          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-foreground">Basic Information</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => updateFormData('firstName', e.target.value)}
                  placeholder="John"
                  className={validationErrors.firstName ? 'border-destructive' : ''}
                />
                {validationErrors.firstName && (
                  <p className="text-sm text-destructive">{validationErrors.firstName}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => updateFormData('lastName', e.target.value)}
                  placeholder="Doe"
                  className={validationErrors.lastName ? 'border-destructive' : ''}
                />
                {validationErrors.lastName && (
                  <p className="text-sm text-destructive">{validationErrors.lastName}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => updateFormData('email', e.target.value)}
                placeholder="john.doe@example.com"
                disabled={isEdit} // Email cannot be changed after creation
                className={validationErrors.email ? 'border-destructive' : ''}
              />
              {validationErrors.email && (
                <p className="text-sm text-destructive">{validationErrors.email}</p>
              )}
              {isEdit && (
                <p className="text-xs text-muted-foreground">Email cannot be changed after creation</p>
              )}
            </div>
          </div>

          {/* Password (only for new users or if explicitly changing) */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">
                {isEdit ? 'Change Password (Optional)' : 'Password *'}
              </h3>
              <div className="flex items-center gap-3">
                {isEdit && (
                  <p className="text-xs text-muted-foreground">Leave blank to keep current password</p>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleGeneratePassword}
                >
                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {passwordCopied ? 'Copied!' : 'Generate'}
                </Button>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => updateFormData('password', e.target.value)}
                    placeholder="••••••••"
                    className={validationErrors.password ? 'border-destructive' : ''}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {validationErrors.password && (
                  <p className="text-sm text-destructive">{validationErrors.password}</p>
                )}
                {!isEdit && (
                  <p className="text-xs text-muted-foreground">
                    At least 8 characters with uppercase, lowercase, and number
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => updateFormData('confirmPassword', e.target.value)}
                  placeholder="••••••••"
                  className={validationErrors.confirmPassword ? 'border-destructive' : ''}
                />
                {validationErrors.confirmPassword && (
                  <p className="text-sm text-destructive">{validationErrors.confirmPassword}</p>
                )}
              </div>
            </div>
          </div>

          {/* Role Selection */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-foreground">Role & Permissions</h3>
            
            <div className="space-y-2">
              <Label htmlFor="role">Role *</Label>
              <Select 
                value={formData.role} 
                onValueChange={(value) => updateFormData('role', value)}
                disabled={isSelf}
              >
                <SelectTrigger className={validationErrors.role ? 'border-destructive' : ''}>
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {roleOptions.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      <div className="flex flex-col">
                        <span className="font-medium">{role.label}</span>
                        <span className="text-xs text-muted-foreground">{role.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {validationErrors.role && (
                <p className="text-sm text-destructive">{validationErrors.role}</p>
              )}
              {isSelf && (
                <p className="text-xs text-muted-foreground">
                  You cannot change your own role.
                </p>
              )}
            </div>

            {formData.role === 'report_viewer' && (
              <ReportPagePermissions
                permissions={formData.permissions || {}}
                onChange={pages => updateFormData('permissions.reportPages', pages)}
              />
            )}

            {/* Additional Permissions (Additive) */}
            {formData.role && formData.role !== 'super_admin' && (
              <div className="space-y-3 pt-2">
                <Label className="text-xs font-medium text-muted-foreground">
                  Additional Permissions (Optional)
                </Label>
                <div className="space-y-2 border border-border rounded-md p-3">
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="perm-pharmacy"
                      checked={formData.permissions?.canManagePharmacy || false}
                      onCheckedChange={() => togglePermission('canManagePharmacy')}
                      disabled={!canGrant('canManagePharmacy')}
                      title={canGrant('canManagePharmacy') ? undefined : 'You can only grant permissions you have'}
                    />
                    <Label htmlFor="perm-pharmacy" className="cursor-pointer font-normal text-sm">
                      <div className="flex flex-col">
                        <span>Pharmacy Management</span>
                        <span className="text-xs text-muted-foreground">
                          Add, edit, and manage pharmacy configurations
                        </span>
                      </div>
                    </Label>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="perm-programs"
                      checked={formData.permissions?.canManagePrograms || false}
                      onCheckedChange={() => togglePermission('canManagePrograms')}
                      disabled={!canGrant('canManagePrograms')}
                      title={canGrant('canManagePrograms') ? undefined : 'You can only grant permissions you have'}
                    />
                    <Label htmlFor="perm-programs" className="cursor-pointer font-normal text-sm">
                      <div className="flex flex-col">
                        <span>Drug Program Management</span>
                        <span className="text-xs text-muted-foreground">
                          Add, edit, and manage drug programs
                        </span>
                      </div>
                    </Label>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="perm-physicians"
                      checked={formData.permissions?.canManagePhysicians || false}
                      onCheckedChange={() => togglePermission('canManagePhysicians')}
                      disabled={!canGrant('canManagePhysicians')}
                      title={canGrant('canManagePhysicians') ? undefined : 'You can only grant permissions you have'}
                    />
                    <Label htmlFor="perm-physicians" className="cursor-pointer font-normal text-sm">
                      <div className="flex flex-col">
                        <span>Physician Management</span>
                        <span className="text-xs text-muted-foreground">
                          Add, edit, and manage physician records
                        </span>
                      </div>
                    </Label>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="perm-releases"
                      checked={formData.permissions?.canManageReleases || false}
                      onCheckedChange={() => togglePermission('canManageReleases')}
                      disabled={!canGrant('canManageReleases')}
                      title={canGrant('canManageReleases') ? undefined : 'You can only grant permissions you have'}
                    />
                    <Label htmlFor="perm-releases" className="cursor-pointer font-normal text-sm">
                      <div className="flex flex-col">
                        <span>Release Management</span>
                        <span className="text-xs text-muted-foreground">
                          Publish and revert configuration releases
                        </span>
                      </div>
                    </Label>
                  </div>

                  {currentUser?.role === 'super_admin' && (
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="perm-shared-physicians"
                      checked={formData.permissions?.canManageSharedPhysicians || false}
                      onCheckedChange={() => togglePermission('canManageSharedPhysicians')}
                    />
                    <Label htmlFor="perm-shared-physicians" className="cursor-pointer font-normal text-sm">
                      <div className="flex flex-col">
                        <span>Shared doctor directory</span>
                        <span className="text-xs text-muted-foreground">
                          Edit and release the doctors every opted-in clinic routes from
                        </span>
                      </div>
                    </Label>
                  </div>
                  )}

                  {currentUser?.role === 'super_admin' && (
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="perm-prescribers"
                      checked={formData.permissions?.canViewPrescribers || false}
                      onCheckedChange={() => togglePermission('canViewPrescribers')}
                    />
                    <Label htmlFor="perm-prescribers" className="cursor-pointer font-normal text-sm">
                      <div className="flex flex-col">
                        <span>Prescribers report</span>
                        <span className="text-xs text-muted-foreground">
                          View payroll script counts for every Express clinic, not just assigned ones
                        </span>
                      </div>
                    </Label>
                  </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Permissions are additive - users can have multiple permissions enabled
                </p>
              </div>
            )}
          </div>

          {/* Clinic Assignment */}
          {formData.role !== 'super_admin' && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-foreground">Clinic Assignment *</h3>
              
              {loadingClinics ? (
                <div className="text-sm text-muted-foreground">Loading clinics...</div>
              ) : availableClinics.length === 0 ? (
                <div className="text-sm text-muted-foreground">No clinics available</div>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto border border-border rounded-md p-3">
                  {availableClinics.map((clinic) => (
                    <div key={clinic.clinicId} className="flex items-center space-x-3">
                      <Checkbox
                        id={`clinic-${clinic.clinicId}`}
                        checked={(formData.clinicIds || []).includes(clinic.clinicId)}
                        onCheckedChange={() => toggleClinic(clinic.clinicId)}
                      />
                      <Label 
                        htmlFor={`clinic-${clinic.clinicId}`}
                        className="flex-1 cursor-pointer font-normal"
                      >
                        <div className="flex flex-col">
                          <span className="font-medium">{clinic.name}</span>
                          <span className="text-xs text-muted-foreground">{clinic.clinicId}</span>
                        </div>
                      </Label>
                    </div>
                  ))}
                </div>
              )}
              {validationErrors.clinicIds && (
                <p className="text-sm text-destructive">{validationErrors.clinicIds}</p>
              )}
            </div>
          )}

          {/* Super Admin Info */}
          {formData.role === 'super_admin' && (
            <div className="space-y-2 p-4 bg-muted/50 rounded-md border border-border">
              <div className="flex items-center space-x-2 text-sm">
                <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium text-foreground">Super Admin Access</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Super admins automatically have access to all clinics in the system. No clinic selection is required.
              </p>
            </div>
          )}

          {/* Status */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-foreground">Status</h3>

            <div className="flex items-center space-x-3">
              <Checkbox
                id="isActive"
                checked={formData.isActive}
                onCheckedChange={(checked) => updateFormData('isActive', checked)}
              />
              <Label htmlFor="isActive" className="cursor-pointer font-normal">
                <div className="flex flex-col">
                  <span>Active User</span>
                  <span className="text-xs text-muted-foreground">
                    Inactive users cannot log in
                  </span>
                </div>
              </Label>
            </div>
          </div>

          {/* TV Display Settings (only for tv_display role) */}
          {formData.role === 'tv_display' && (
            <div className="space-y-4 p-4 bg-blue-950/20 rounded-lg border border-blue-500/30">
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <h3 className="text-sm font-semibold text-foreground">TV Display Settings</h3>
              </div>

              {/* Display Clinics */}
              <div className="space-y-2">
                <Label className="text-xs font-medium">Display Clinics</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Select which clinics to show on the TV display. Leave empty to show all assigned clinics.
                </p>
                {loadingClinics ? (
                  <div className="text-sm text-muted-foreground">Loading clinics...</div>
                ) : (
                  <div className="space-y-2 max-h-32 overflow-y-auto border border-border rounded-md p-2 bg-background">
                    {availableClinics
                      .filter(clinic => formData.clinicIds?.includes(clinic.clinicId))
                      .map((clinic) => (
                        <div key={`tv-${clinic.clinicId}`} className="flex items-center space-x-2">
                          <Checkbox
                            id={`tv-clinic-${clinic.clinicId}`}
                            checked={(formData.tvDisplaySettings?.displayClinicIds || []).includes(clinic.clinicId)}
                            onCheckedChange={() => toggleTvDisplayClinic(clinic.clinicId)}
                          />
                          <Label
                            htmlFor={`tv-clinic-${clinic.clinicId}`}
                            className="cursor-pointer font-normal text-sm"
                          >
                            {clinic.name}
                          </Label>
                        </div>
                      ))}
                    {formData.clinicIds?.length === 0 && (
                      <p className="text-xs text-muted-foreground">Assign clinics above first</p>
                    )}
                  </div>
                )}
              </div>

              {/* Date Range */}
              <div className="space-y-2">
                <Label htmlFor="tvDateRange" className="text-xs font-medium">Date Range</Label>
                <Select
                  value={formData.tvDisplaySettings?.dateRange || 'last30'}
                  onValueChange={(value) => updateTvDisplaySetting('dateRange', value)}
                >
                  <SelectTrigger id="tvDateRange">
                    <SelectValue placeholder="Select date range" />
                  </SelectTrigger>
                  <SelectContent>
                    {DATE_RANGE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Refresh Interval */}
              <div className="space-y-2">
                <Label htmlFor="tvRefreshInterval" className="text-xs font-medium">
                  Refresh Interval: {formData.tvDisplaySettings?.refreshInterval || 15} minutes
                </Label>
                <input
                  id="tvRefreshInterval"
                  type="range"
                  min="5"
                  max="60"
                  step="5"
                  value={formData.tvDisplaySettings?.refreshInterval || 15}
                  onChange={(e) => updateTvDisplaySetting('refreshInterval', parseInt(e.target.value))}
                  className="w-full accent-blue-500"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>5 min</span>
                  <span>60 min</span>
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (Optional)</Label>
            <textarea
              id="notes"
              value={formData.metadata.notes}
              onChange={(e) => updateFormData('metadata.notes', e.target.value)}
              placeholder="Add any additional notes about this user..."
              className="w-full min-h-20 px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground">
              {formData.metadata.notes?.length || 0}/500 characters
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : (
              <>{isEdit ? 'Update User' : 'Create User'}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

